/**
 * Platform API client — PRD v1.1 §34.
 *
 * Two rules this file exists to hold:
 *
 *  - **D11.** The server decides whether a session is charging, via `is_charging`.
 *    The UI must never derive that from the state string itself. `ACCEPTED` and
 *    `STARTING` are not charging, and showing 充電中 there is how a driver walks away
 *    from a car that never started.
 *  - **§34.2.** Every mutating call carries an `Idempotency-Key`. Mobile networks
 *    retry; charging and payment must not.
 */

const BASE = '/api/v1'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** Server-supplied, already customer-legible per §21. Safe to display verbatim. */
    message: string,
  ) {
    super(message)
  }
}

let bearer: string | null = null
export function setToken(token: string) {
  bearer = token
}

/** A stable key per logical action, so a retry is the *same* request (§8.3). */
export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  idempotencyKey?: string,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  }
  if (bearer) headers.Authorization = `Bearer ${bearer}`
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey

  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, { ...init, headers })
  } catch {
    // A network failure tells us nothing about whether the server acted. Never
    // report it as "did not happen" (§8.6).
    throw new ApiError(0, '網路連線中斷，請稍後再試。')
  }

  if (res.status === 204) return undefined as T
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    const detail = typeof body?.detail === 'string' ? body.detail : '發生未預期的錯誤。'
    throw new ApiError(res.status, detail)
  }
  return body as T
}

// ---------------------------------------------------------------------------
// types — mirror the FastAPI response models
// ---------------------------------------------------------------------------

export interface Connector {
  connector_id: number
  status: string
  available: boolean
  observed_at: string | null
  stale: boolean
}

export interface ScanResult {
  evse_id: string
  connector_id: number | null
  station_id: string | null
  station_name: string | null
  format_id: string
  needs_connector_choice: boolean
  online: boolean
  connectors: Connector[]
  tariff_id: string | null
}

export interface SessionView {
  session_id: string
  state: string
  /** Authoritative. Do not infer this from `state` (D11). */
  is_charging: boolean
  evse_id: string
  connector_id: number
  station_name: string | null
  started_at: string | null
  energy_kwh: string | null
  message: string | null
}

export interface SessionStatus extends SessionView {
  ended_at: string | null
  duration_seconds: number | null
  power_w: number | null
  estimated_fee_twd: number | null
  /** Always true while charging — the UI must label the figure 預估 (§16). */
  estimated: boolean
  rate: string | null
  rate_unit: string | null
  data_age_seconds: number | null
}

export interface HistoryItem {
  session_id: string
  started_at: string | null
  ended_at: string | null
  station_name: string | null
  evse_id: string
  connector_id: number
  energy_kwh: string | null
  duration_seconds: number | null
  state: string
}

// ---------------------------------------------------------------------------

export const api = {
  login: (idToken: string) =>
    request<{ customer_id: string; display_name: string | null; id_tag: string }>(
      '/auth/line',
      { method: 'POST', body: JSON.stringify({ id_token: idToken }) },
    ),

  parseQr: (payload: string) =>
    request<ScanResult>('/qr/parse', {
      method: 'POST',
      body: JSON.stringify({ payload }),
    }),

  startCharging: (evseId: string, connectorId: number, key: string) =>
    request<SessionView>(
      '/charging/sessions',
      {
        method: 'POST',
        body: JSON.stringify({ evse_id: evseId, connector_id: connectorId }),
      },
      key,
    ),

  activeSession: () => request<SessionView | null>('/charging/sessions/active'),

  status: (sessionId: string) =>
    request<SessionStatus>(`/charging/sessions/${sessionId}/status`),

  stopCharging: (sessionId: string, key: string) =>
    request<SessionView>(
      `/charging/sessions/${sessionId}/stop`,
      { method: 'POST' },
      key,
    ),

  history: (limit = 20) => request<HistoryItem[]>(`/history?limit=${limit}`),
}
